import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { api, API_BASE, tokenStore } from "@/lib/api";

/** CSV lead importer — admins only. Handles duplicate detection by phone. */
export default function ImportLeadsDialog({ trigger }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [onDuplicate, setOnDuplicate] = useState("skip");
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const qc = useQueryClient();

  const importMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(
        `/leads/import?on_duplicate=${onDuplicate}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    },
    onSuccess: (data) => {
      setResult(data);
      const total = (data.created || 0) + (data.updated || 0);
      toast.success(
        `Imported ${data.created}` +
          (data.updated ? ` · updated ${data.updated}` : "") +
          (data.duplicates_count ? ` · ${data.duplicates_count} duplicate${data.duplicates_count === 1 ? "" : "s"}` : "")
      );
      if (total > 0) {
        qc.invalidateQueries({ queryKey: ["leads"] });
        qc.invalidateQueries({ queryKey: ["dashboard"] });
        qc.invalidateQueries({ queryKey: ["locations"] });
      }
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Import failed"),
  });

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("Please choose a .csv file");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API_BASE}/leads/import/template`, {
        headers: { Authorization: `Bearer ${tokenStore.get()}` },
      });
      const txt = await res.text();
      const blob = new Blob([txt], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ipc-leads-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template");
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle>Import leads from CSV</DialogTitle>
          <DialogDescription>
            Bulk upload prospects (Google Ads, Housing.com, MagicBricks exports). Required columns:{" "}
            <span className="font-medium">name</span> and{" "}
            <span className="font-medium">phone</span>.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                pickFile(e.dataTransfer.files?.[0]);
              }}
              onClick={() => inputRef.current?.click()}
              data-testid="import-dropzone"
              className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed p-8 text-center transition ${
                dragOver
                  ? "border-[hsl(var(--brand-navy))] bg-slate-50"
                  : "border-slate-200 bg-slate-50/50"
              }`}
            >
              <input
                ref={inputRef}
                data-testid="import-file-input"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
              {file ? (
                <>
                  <FileSpreadsheet className="h-10 w-10 text-emerald-600" />
                  <div className="text-sm font-semibold text-navy">{file.name}</div>
                  <div className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
                </>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-slate-400" />
                  <div className="text-sm font-medium text-slate-700">
                    Drop your CSV here or click to browse
                  </div>
                  <div className="text-xs text-slate-500">.csv only, up to a few MB</div>
                </>
              )}
            </div>

            {/* Duplicate strategy */}
            <div className="soft-card space-y-2 p-3" data-testid="dup-strategy">
              <div className="mb-1 text-xs font-semibold text-slate-600">
                If a phone number already exists…
              </div>
              <RadioGroup value={onDuplicate} onValueChange={setOnDuplicate} className="space-y-1">
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <RadioGroupItem value="skip" id="dup-skip" data-testid="dup-skip" className="mt-0.5" />
                  <div>
                    <span className="font-medium text-slate-800">Skip &amp; flag as duplicate</span>
                    <div className="text-[11px] text-slate-500">Safest default — keeps your existing record.</div>
                  </div>
                </label>
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <RadioGroupItem value="update" id="dup-update" data-testid="dup-update" className="mt-0.5" />
                  <div>
                    <span className="font-medium text-slate-800">Update existing lead</span>
                    <div className="text-[11px] text-slate-500">Overwrite with fresh values from the CSV.</div>
                  </div>
                </label>
                <label className="flex cursor-pointer items-start gap-2 text-sm">
                  <RadioGroupItem value="create" id="dup-create" data-testid="dup-create" className="mt-0.5" />
                  <div>
                    <span className="font-medium text-slate-800">Import anyway</span>
                    <div className="text-[11px] text-slate-500">Create a new record even if the phone is a duplicate.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            <button
              data-testid="import-download-template"
              type="button"
              onClick={downloadTemplate}
              className="inline-flex items-center gap-2 self-start text-xs font-semibold text-navy hover:underline"
            >
              <Download className="h-3 w-3" /> Download CSV template
            </button>
          </>
        )}

        {result && (
          <div className="space-y-3" data-testid="import-result">
            <div className="soft-card flex items-center gap-3 p-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              <div className="flex-1">
                <div className="text-sm font-bold text-navy">
                  {result.created} created
                  {result.updated ? ` · ${result.updated} updated` : ""}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {result.duplicates_count > 0 && (
                    <span data-testid="result-duplicates">
                      {result.duplicates_count} duplicate{result.duplicates_count === 1 ? "" : "s"}
                      {result.on_duplicate === "skip" ? " skipped" : ""}
                      {result.skipped > 0 ? " · " : ""}
                    </span>
                  )}
                  {result.skipped > 0 && `${result.skipped} row${result.skipped === 1 ? "" : "s"} skipped`}
                </div>
              </div>
            </div>

            {result.duplicates?.length > 0 && (
              <div className="soft-card max-h-40 overflow-y-auto p-3" data-testid="result-dup-list">
                <div className="mb-2 flex items-center gap-1 text-xs font-bold text-amber-700">
                  <Users className="h-3.5 w-3.5" /> Duplicate phone numbers (first {result.duplicates.length})
                </div>
                <ul className="space-y-1 text-[12px] text-slate-600">
                  {result.duplicates.map((d) => (
                    <li key={`dup-${d.row}`}>
                      Row {d.row}: <span className="font-mono">{d.phone}</span> ·{" "}
                      <span className="font-medium">{d.incoming_name}</span>{" "}
                      → already exists as{" "}
                      <span className="font-medium">{d.existing_name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div className="soft-card max-h-40 overflow-y-auto p-3">
                <div className="mb-2 flex items-center gap-1 text-xs font-bold text-amber-700">
                  <AlertTriangle className="h-3.5 w-3.5" /> Skipped rows
                </div>
                <ul className="space-y-1 text-[12px] text-slate-600">
                  {result.errors.slice(0, 20).map((e) => (
                    <li key={`err-${e.row}`}>
                      Row {e.row}: {e.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="import-cancel">
                Cancel
              </Button>
              <Button
                data-testid="import-submit"
                className="bg-navy hover:brightness-110"
                disabled={!file || importMut.isPending}
                onClick={() => importMut.mutate()}
              >
                {importMut.isPending ? "Uploading…" : "Import"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={reset} data-testid="import-again">
                Import another
              </Button>
              <Button
                data-testid="import-done"
                className="bg-navy hover:brightness-110"
                onClick={() => setOpen(false)}
              >
                Done
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
